/**
 * <s-dispatch-estimate> — clock-aware dispatch line for ready-to-ship products
 * (see snippets/shipping-estimate.liquid). Computes, in the store's time zone, whether an
 * order placed now ships today (dispatch day and before the cutoff) or on the next dispatch
 * day, and rewrites its text. Re-checks every minute, so the line flips at the cutoff
 * while the page is open. Holidays are not known here; use the entry's Observação for those.
 *
 * Attributes:
 *   data-cutoff        — "HH:MM" (store time).
 *   data-days          — comma-separated weekdays, 0 = Sunday … 6 = Saturday.
 *   data-timezone      — IANA zone (America/Sao_Paulo).
 *   data-today         — template with __TIME__ ("hoje, para pedidos até __TIME__").
 *   data-tomorrow      — "amanhã".
 *   data-time          — the cutoff already formatted for display ("15h").
 * The next-day name comes from Intl in the page language ("segunda-feira").
 */
const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

class SDispatchEstimate extends HTMLElement {
  #timer;

  connectedCallback() {
    this.#render();
    this.#timer = setInterval(() => this.#render(), 60_000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #storeNow(timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value;
    return {
      weekday: WEEKDAYS[get('weekday')],
      minutes: Number(get('hour')) * 60 + Number(get('minute')),
    };
  }

  #render() {
    const timeZone = this.dataset.timezone || 'America/Sao_Paulo';
    const days = (this.dataset.days || '').split(',').filter(Boolean).map(Number);
    const [cutHour, cutMinute] = (this.dataset.cutoff || '').split(':').map(Number);
    if (!days.length || !Number.isFinite(cutHour)) return;

    const now = this.#storeNow(timeZone);
    if (now.weekday === undefined) return;
    const cutoff = cutHour * 60 + (cutMinute || 0);

    if (days.includes(now.weekday) && now.minutes < cutoff) {
      this.textContent = (this.dataset.today || '').replace('__TIME__', this.dataset.time || '');
      return;
    }

    for (let offset = 1; offset <= 7; offset += 1) {
      if (!days.includes((now.weekday + offset) % 7)) continue;
      if (offset === 1) {
        this.textContent = this.dataset.tomorrow || '';
      } else {
        const target = new Date(Date.now() + offset * 86_400_000);
        this.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'pt-BR', {
          weekday: 'long',
          timeZone,
        }).format(target);
      }
      return;
    }
  }
}

if (!customElements.get('s-dispatch-estimate')) {
  customElements.define('s-dispatch-estimate', SDispatchEstimate);
}
